import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { DebugKey, createDebug } from '@ps/web-core-utils';
import { Button, Translation } from '@ps/web-shared-components';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import type { StarsWeb } from '@swf/core/types/Window';

import {
  TXTWCL_CanXt_find_what_youXre_looking_ELL,
  TXTWCL_Contact_Us
} from './ContactUs.messages.json';
import styles from './ContactUs.scss';

const chEmail = 'support@casino777.ch';

const debug = createDebug(DebugKey.Embedded);

const ContactUs: React.FC = () => {
  const { modal } = useDispatch<Dispatch>();
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const modalState = useSelector(select.modal.getCurrent);
  const shouldOpenSFChat = useSelector(select.help.getShouldOpenSFChat);
  const shouldOpenMessenger = useSelector(select.help.getShouldOpenMessenger);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const site = useSelector(select.cms.getSiteId);
  const messengerIgnoreLogin = useFeature(FeatureFlag.PSMessengerIgnoreLogin);
  const hidePrompt = useFeature(FeatureFlag.SFHideLoginPrompt);
  const {
    help: { setOpenChat }
  } = useDispatch<Dispatch>();

  // Track if we're currently opening chat to prevent multiple attempts
  const isOpeningChatRef = useRef<boolean>(false);

  useEffect(() => {
    if (modalState === undefined) {
      setOpenChat({ openChat: false });
    }
  }, [modalState, setOpenChat]);

  const openLPMessenger = useCallback(() => {
    if (!isAuthenticated && messengerIgnoreLogin) {
      window.lpTagMgr.openChat();
    } else if (isAuthenticated) {
      window.lpTagMgr.openAuthenticatedChat();
    } else {
      setOpenChat({ openChat: true });
      modal.enqueue({ path: '/help/messenger' });
    }
  }, [isAuthenticated, messengerIgnoreLogin, modal, setOpenChat]);

  const openContactSupportForm = useCallback(() => {
    if (isDesktopEmbedded) {
      const starsweb = window.starsweb as StarsWeb;
      starsweb?.app.execute('support');
    } else if (isMobileEmbedded) {
      const starsweb = window.starsweb as StarsWeb;
      starsweb?.app.execute('support/contact-support');
    } else {
      modal.enqueue({ path: `/help/contact` });
    }
  }, [isDesktopEmbedded, isMobileEmbedded, modal]);

  const openMIAW = useCallback(async () => {
    // Prevent multiple simultaneous attempts
    if (isOpeningChatRef.current) {
      debug('openMIAW: Already attempting to open chat, ignoring click');
      return;
    }

    isOpeningChatRef.current = true;

    debug(
      'openMIAW: attempting to launch Salesforce chat (auth: %s)',
      isAuthenticated ? 'authenticated' : 'unauthenticated'
    );

    // Add breadcrumb for chat button click
    Sentry.addBreadcrumb({
      message: 'Salesforce Chat button clicked',
      category: 'salesforce.chat',
      level: 'info',
      data: {
        isAuthenticated,
        messengerIgnoreLogin,
        hidePrompt
      }
    });

    // Retry logic with exponential backoff
    const maxRetries = 10;
    const maxTotalTime = 10000; // 10 seconds
    const startTime = Date.now();
    let retryCount = 0;
    let delay = 100; // Start with 100ms

    const attemptLaunchChat = async (): Promise<boolean> => {
      if (window.embeddedservice_bootstrap?.utilAPI?.launchChat) {
        debug('openMIAW: utilAPI.launchChat is available, launching chat');

        try {
          if (!isAuthenticated && messengerIgnoreLogin) {
            window.embeddedservice_bootstrap.utilAPI.launchChat(true);
          } else if (!isAuthenticated && hidePrompt) {
            window.embeddedservice_bootstrap.utilAPI.launchChat(true);
          } else if (isAuthenticated) {
            window.embeddedservice_bootstrap.utilAPI.launchChat(true);
          } else {
            modal.enqueue({ path: '/help/messenger' });
          }
          return true;
        } catch (error) {
          debug('openMIAW: Error calling launchChat', error);
          Sentry.captureException(error, {
            tags: {
              'salesforce.operation': 'launchChat'
            }
          });
          return false;
        }
      }
      return false;
    };

    // Try to launch chat with retries
    while (retryCount < maxRetries && Date.now() - startTime < maxTotalTime) {
      if (await attemptLaunchChat()) {
        isOpeningChatRef.current = false;
        return;
      }

      retryCount++;

      // Add breadcrumb for retry
      Sentry.addBreadcrumb({
        message: `Salesforce SDK not ready, retry attempt ${retryCount}`,
        category: 'salesforce.chat',
        level: 'info',
        data: {
          retryCount,
          delay,
          hasBootstrap: !!window.embeddedservice_bootstrap,
          hasUtilAPI: !!window.embeddedservice_bootstrap?.utilAPI,
          hasLaunchChat: !!window.embeddedservice_bootstrap?.utilAPI?.launchChat
        }
      });

      // Wait before next retry
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Exponential backoff with cap at 1 second
      delay = Math.min(delay * 2, 1000);
    }

    // Failed after all retries
    const timeElapsed = Date.now() - startTime;
    const errorMessage = `Salesforce SDK failed to initialize after ${timeElapsed}ms (${retryCount} retries)`;

    debug('openMIAW: %s', errorMessage);

    Sentry.captureException(new Error(errorMessage), {
      tags: {
        'salesforce.operation': 'launchChat',
        'salesforce.failure': 'sdk_not_ready'
      },
      extra: {
        retryCount,
        timeElapsed,
        isAuthenticated,
        hasBootstrap: !!window.embeddedservice_bootstrap,
        hasUtilAPI: !!window.embeddedservice_bootstrap?.utilAPI,
        hasLaunchChat: !!window.embeddedservice_bootstrap?.utilAPI?.launchChat
      }
    });

    isOpeningChatRef.current = false;
  }, [isAuthenticated, hidePrompt, messengerIgnoreLogin, modal]);

  const handleClick = useCallback(() => {
    if (site === Sites.CH) {
      window.location.href = `mailto:${chEmail}`;
      return;
    }

    if (shouldOpenSFChat) {
      openMIAW();
    } else if (shouldOpenMessenger) {
      openLPMessenger();
    } else {
      openContactSupportForm();
    }
  }, [
    site,
    shouldOpenSFChat,
    shouldOpenMessenger,
    openLPMessenger,
    openContactSupportForm,
    openMIAW
  ]);

  return (
    <section className={styles.container}>
      <h3>
        <Translation {...TXTWCL_CanXt_find_what_youXre_looking_ELL} />
      </h3>
      <Button translation={TXTWCL_Contact_Us} onPress={handleClick} />
    </section>
  );
};

export default ContactUs;
